import { useQuery } from '@tanstack/react-query';
import { getToken } from '../../utils/auth';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { ArrowPathIcon } from '@heroicons/react/24/outline';

import useQueryParams from '../../hooks/useQueryParams';
import CheckListWidget from '../common/CheckListWidget';

const { VITE_API_SERVICE } = import.meta.env;

export default function ReportMetrics() {
  const { setQueryParam, removeQueryParam, getParam } = useQueryParams();
  const [selectedMetrics, setSelectedMetrics] = useState(() => {
    const metrics = getParam('metrics');
    return metrics ? new Set(metrics.split(',')) : new Set();
  });

  const location = useLocation();

  /**
   * Handle metric change
   * @param {string} metricId
   * @param {boolean} checked
   */
  function handleMetricChange(metricId, checked) {
    const newMetrics = new Set(selectedMetrics);
    if (checked) {
      newMetrics.add(metricId);
    } else {
      newMetrics.delete(metricId);
    }

    setSelectedMetrics(newMetrics);

    if (newMetrics.size > 0) {
      setQueryParam('metrics', Array.from(newMetrics).join(','));
    } else {
      removeQueryParam('metrics');
    }
  }

  /**
   * Clear all metrics
   */
  function clearAllMetrics() {
    setSelectedMetrics(new Set());
    removeQueryParam('metrics');
  }

  /**
   * Select all metrics
   */
  function selectAllMetrics() {
    if (!data?.metrics) return;
    const allMetricIds = new Set(data.metrics.map((m) => m.id));
    setSelectedMetrics(allMetricIds);
    setQueryParam('metrics', Array.from(allMetricIds).join(','));
  }

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['metrics'],
    queryFn: async function () {
      const token = getToken();

      const response = await fetch(`${VITE_API_SERVICE}/v1/reports/metrics`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(data.message);
        }

        throw new Error('Failed to fetch metrics');
      }

      return data;
    }
  });

  useEffect(() => {
    if (data?.metrics) {
      const metrics = getParam('metrics');
      if (metrics) {
        // Convert string metrics to numbers and filter out any invalid ones
        const validMetrics = metrics
          .split(',')
          .map(Number)
          .filter((id) => !isNaN(id) && data.metrics.some((m) => m.id === id));

        if (validMetrics.length > 0) {
          setSelectedMetrics(new Set(validMetrics));
          setQueryParam('metrics', validMetrics.join(','));
        } else {
          // If no valid metrics found, set defaults
          const defaultMetrics = new Set([
            'objectiveCountableSum',
            'inventory',
            'supplyRevenueInUSD'
          ]);
          setSelectedMetrics(defaultMetrics);
          setQueryParam('metrics', Array.from(defaultMetrics).join(','));
        }
      } else {
        // If no metrics in URL, set defaults
        const defaultMetrics = new Set([
          'objectiveCountableSum',
          'inventory',
          'supplyRevenueInUSD'
        ]);
        setSelectedMetrics(defaultMetrics);
        setQueryParam('metrics', Array.from(defaultMetrics).join(','));
      }
    }
  }, [data?.metrics, location.state?.reload]);

  // Watch for URL parameter changes
  useEffect(() => {
    const metrics = getParam('metrics');
    if (!metrics) {
      setSelectedMetrics(new Set());
    }
  }, [getParam('metrics')]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <span className="loading loading-spinner loading-md"></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="collapse collapse-arrow shadow-sm">
        <input type="checkbox" defaultChecked />
        <div className="collapse-title font-medium">
          <h4>Metrics</h4>
        </div>
        <div className="collapse-content">
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">
                {error.message}
              </p>
              <button
                onClick={() => refetch()}
                className="btn btn-sm btn-ghost gap-2"
                aria-label="Retry loading metrics"
              >
                <ArrowPathIcon className="h-4 w-4" />
                <span className="text-sm">Retry</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CheckListWidget
      title="Metrics"
      searchPlaceholder="Search metrics..."
      items={data.metrics}
      selectedItems={selectedMetrics}
      onItemChange={handleMetricChange}
      onClearAll={clearAllMetrics}
      onSelectAll={selectAllMetrics}
    />
  );
}
